import { OptionallyVisible } from 'components';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/buttons';
import { Panel } from 'components/containers';

import { AccountLandingPageSettings } from 'types/account';

import { WidgetPreview } from '../components/widget/widget';
import { LandingSettingsForm } from './form/form';
import { LABELS } from './keys';
import { Controls, FormContainer, PanelContent } from './styles';

interface GeneralSectionProps {
  account: AccountLandingPageSettings;
  initialValues: AccountLandingPageSettings;
  isFormChanged: boolean;
  isSaveAvailable: boolean;
  resetFormValues: () => void;
  saveFormValues: () => void;
  onFormValueChange: (key: string, value: any) => void;
  disabledFields: Record<string, boolean>;
  isUpdating: boolean;
  error?: Record<string, string>;
  readOnlyMode: boolean;
  previewOptions: {
    publicKey?: string;
    seed: string;
  };
}

export const LandingSection = ({
  account,
  initialValues,
  isSaveAvailable,
  isFormChanged,
  resetFormValues,
  onFormValueChange,
  saveFormValues,
  disabledFields,
  isUpdating,
  error,
  readOnlyMode,
  previewOptions,
}: GeneralSectionProps) => {
  const { t } = useTranslation();

  return (
    <Panel
      label={t(LABELS.PANEL_TITLE)}
      controls={
        readOnlyMode ? undefined : (
          <Controls>
            <OptionallyVisible visible={isFormChanged}>
              <Button onClick={resetFormValues} secondary disabled={isUpdating}>
                {t(LABELS.CANCEL_BUTTON_LABEL)}
              </Button>
            </OptionallyVisible>
            <Button onClick={saveFormValues} primary disabled={!isSaveAvailable || isUpdating}>
              {t(LABELS.SAVE_BUTTON_LABEL)}
            </Button>
          </Controls>
        )
      }
    >
      <PanelContent>
        <FormContainer>
          <LandingSettingsForm
            account={account}
            initialValues={initialValues}
            onFieldValueChange={onFormValueChange}
            disabledFields={disabledFields}
            isUpdating={isUpdating}
            error={error}
            readOnlyMode={readOnlyMode}
          />
        </FormContainer>
        <OptionallyVisible visible={Boolean(account.landingPageEnabled && initialValues.urlSlug)}>
          <WidgetPreview params={{ ...account, urlSlug: initialValues.urlSlug }} {...previewOptions} asLanding />
        </OptionallyVisible>
      </PanelContent>
    </Panel>
  );
};
